import { useState, useEffect } from 'react';
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Topic } from '@/types/topic';
import { useAuth } from "@/contexts/AuthContext";

export function useTopic(taskType: number, draftId: string | null) {
  const [selectedTopicId, setSelectedTopicId] = useState<string>("");
  const { user } = useAuth();

  const { data: topics = [], isLoading: isLoadingTopics } = useQuery<Topic[]>({
    queryKey: ["topics", taskType],
    queryFn: async () => {
      if (!user) throw new Error("No authenticated user");

      const { data, error } = await supabase
        .from("topics")
        .select("*")
        .eq("task_type", taskType)
        .order("created_at");

      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    if (draftId && topics) {
      const loadDraftTopic = async () => {
        const { data, error } = await supabase
          .from("drafts")
          .select("topic_id")
          .eq("id", draftId)
          .single();

        if (error) {
          toast.error("Failed to load draft");
          return;
        }

        if (data?.topic_id && topics.some(t => t.id === data.topic_id)) {
          setSelectedTopicId(data.topic_id);
        }
      };

      loadDraftTopic();
    }
  }, [draftId, topics]);

  const handleRandomTopic = () => {
    if (topics && topics.length > 0) {
      const randomIndex = Math.floor(Math.random() * topics.length);
      setSelectedTopicId(topics[randomIndex].id);
    }
  };

  const selectedTopic = topics?.find((t) => t.id === selectedTopicId);

  return {
    topics,
    selectedTopic,
    selectedTopicId,
    setSelectedTopicId,
    handleRandomTopic,
    isLoadingTopics,
  };
}